<!-- OpenGraph image: https://media.githubusercontent.com/media/wrightwriter/wrightwriter.github.io/master/images/vestige_gfx.png -->
<!-- OpenGraph description: Writeup on vestige. -->


<BlogTitle title={title} date={date} />

<article>

## **Introduction**
[Revision 2025](https://2025.revision-party.net/) was awesome! Was great meeting everyone in person and I'm looking forward to next year :)   

I contributed with a 4kb intro called vestige.
  
The .exe is available [here](https://github.com/wrightwriter/demo-releases/releases/download/vestige/vestige.zip). It needs monitor set to 60hz and a fairly good GPU. I highly recommend running it, as it's a bit of an encoder killer.
It's also on [pouet](https://www.pouet.net/prod.php?which=103916) and [github](https://github.com/wrightwriter/vestige).
  
<brb />  

<YoutubeVideo src={`nUQ-aP4SkDQ`}/>

<brb />  

## **Music**

Written in a compute shader. Amounts to 44100kHz \* 60s \* 8m \* 2ch \* 32bits = 170 megs. Takes 2 seconds to generate on an rtx 4080 super.  

  
[*Intro*](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/music.comp#L147) - additive synthesis inspired by [Oidos](https://github.com/askeksa/Oidos).
- 2400 times:
- A frequency is picked - random note in the scale.   
- It's transposed up/down a couple of octaves, randomly. 
- Time is offset a bit for a strumming feel.
- Between 2 and 20 sine wave overtones are generated in the harmonic series.
- Some chorus and FM are applied.
- 2d rotation matrix is used for stereo width.
- The "popping" occurs due to modulation of parameters. Left in as a stylistic choice.


[*Arp Lead*](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/music.comp#L242) - FM-ed sine wave with some reverb. 
- Time repeats every "repd", which itself is modulated by sin(t). This is how the arpeggio speeds up and slows down.

```glsl
float repd = .5 + sin(t/2)*2/t;
```
- One of 4 notes is picked, detuned and randomly transposed up/down an octave.
- A simple sine wave with FM is generated.
- Reverb is done by repeating the above 400 times, while offsetting time and applying chorus (phase modulation) on the sine wave. This creates a lot of undesirable phase cancellation, so the signal has to be boosted.

[*Sub*](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/music.comp#L366) - sine wave.
- All the sub throughout the track is made of sine waves, with occasional FM and waveshaping.
- A tanh() soft clipper is applied on the master channel. The sub pumps hard into it, distorting the whole track. This both adds gritty overtones and feels kinda like a sidechain.

[*Supersaw? or whatever that is.*](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/music.comp#L311)
- 4*44 times:
- One of four notes is picked.
- It's transposed up/down randomly.
- It's detuned.
- [Cyclic noise](https://scrapbox.io/0b5vr/Cyclic_Noise) is used to create a wavetable oscillator. [Trick by 0b5vr](https://www.shadertoy.com/view/cdjyDc), used in his intros and also [Nusan's 4k](https://www.pouet.net/prod.php?which=104049) this Revision.
- 2d rotation matrix applied for stereo width.

[Granular resynthesis of the whole track.](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/music.comp#L104)   
- There are separate time variables for sequencing and for oscillator phase.   
- This allows pausing/"stretching"/rewinding of the track, while keeping the pitch intact!
- The whole second half of the intro is this plus a sub thumping through the tanh() on master.

<!-- - chords are additive
- outro is granular resynthesis (float time, float envtime) -->

## **Visuals**
The whole intro is a modified [compute.toys shader](https://compute.toys/view/154) which I wrote 1-2 years ago.   
   
Rendering is done using additive atomic point splatting. I wrote about that [here](https://wrighter.xyz/blog/2023_08_17_flame_fractals_in_comp_shader) and [here](https://wrighter.xyz/blog/2023_08_18_forward_pathtracing_in_comp_shader).   
   
[compute.toys](https://compute.toys/) and [Bonzomatic Compute](https://github.com/wrightwriter/Bonzomatic-Compute) are tools where you can get that running quite quickly. With some examples [here](https://gist.github.com/wrightwriter) and [here](https://github.com/evvvvil/bonzomatic-compute-examples?tab=readme-ov-file).
  
	  
[*Drawing points*](https://github.com/wrightwriter/vestige/blob/master/src/shaders/render.comp)
- I had a specific algorithm in my head, but it bugged out and I liked how that looked :)
- It creates the "winged" creature by rotating an "advection" vector every step, whose angle depends on an RNG.
- In some segments, the point is run through a [broken pmod()](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/render.comp#L42) function. I forgot to wrap the vector in brackets inside the macro, but ended up looking cool.
- It's then occasionally [run in worldspace through a sin() and/or through sphere inversion](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/render.comp#L178) before splatting.
- After projection to screen space, the position is moduloed, instead of rejecting out of viewport points.
- On splatting, I intentionally not only add to the correct pixel, but also to an [offset position](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/render.comp#L226). This creates some of the dithery look. There is no dithering done in the intro.

- In the latter half of the intro, the "TV static" effect is done by drawing the same exact stuff as before, but applying a very wide [stochastic "horizontal" depth of field](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/render.comp#L215).

  
[*Post processing*](https://github.com/wrightwriter/vestige/blob/master/src/shaders/post.frag)

- The histogram is read and run through some [tonemapping/gamma stuff](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/post.frag#L142). 
- The colours are all created in post process, and picked from a small [palette](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/post.frag#L166).
- Sometimes, the histogram is [cleared](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/post.frag#L178).
- Sometimes it's not, and the current pixel is [set to another pixel's value](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/post.frag#L181).
- A [monochrome filter](https://github.com/wrightwriter/vestige/blob/8f08cec1038518e7c9d45809aeb0f618eb41218d/src/shaders/post.frag#L217) is applied occasionally.
- An important trick was to have a virtual border around the screen. The histogram is actually in a resolution a bit higher than 720p. The pixels inside the border are never cleared, but still drawn to, and that allows them to leak into the feedback.


## **Process**

The intro found itself throughout the process. It asked to be 8 minutes, it asked for the story and theme. The music wanted to sound like this and the visuals needed to compliment it like this. I let myself go.
  
	  
This is very much counter to how "[bruh i ain't naming things](https://www.pouet.net/prod.php?which=101164)" was produced and was a way more personal (and enjoyable) process. 


## **Size**

Around 1kb for cpp, 1kb for audio and 2kb for visuals. 



















</article>

<script lang="ts">
	import {onDestroy} from 'svelte'
	import { onMount } from "svelte"
	import Video from "../utils/Video.svelte"
	import Image from "../utils/Image.svelte"
	import SubTitle from "../utils/SubTitle.svelte"
	import BlogTitle from "../utils/BlogTitle.svelte"
  
  export let date = new Date("2025-04-26")
  export let title = "vestige - 4k intro writeup"
  
	import YoutubeVideo from "components/utils/YoutubeVideo.svelte"

	onDestroy(() => {})
	onMount(async () => {
	})
</script>

<style lang="scss">

</style>

